import { getFrame, parseTexturePackerHash } from "./atlas";
import {
  REQUIRED_SPRITE_IDS,
  type AssetLoadState,
  type AtlasManifest,
  type SpriteId
} from "./types";

type FxSequenceId = "fx_pop" | "fx_explosion" | "fx_wipewave" | "fx_rotateflash";

const RAW_FX_SEQUENCE_FRAMES: Record<FxSequenceId, string[]> = {
  fx_pop: ["fx_pop_1", "fx_pop_2", "fx_pop_3", "fx_pop_4"],
  fx_explosion: [
    "fx_explosion_1",
    "fx_explosion_2",
    "fx_explosion_3",
    "fx_explosion_4",
    "fx_explosion_5",
    "fx_explosion_6",
    "fx_explosion_7",
    "fx_explosion_8"
  ],
  fx_wipewave: ["fx_wipewave_1", "fx_wipewave_2", "fx_wipewave_3", "fx_wipewave_4"],
  fx_rotateflash: ["fx_rotateflash_1", "fx_rotateflash_2", "fx_rotateflash_3", "fx_rotateflash_4"]
};

function isFxSequenceId(id: string): id is FxSequenceId {
  return id === "fx_pop" || id === "fx_explosion" || id === "fx_wipewave" || id === "fx_rotateflash";
}

function resolveImageUrl(imagePath: string): string {
  if (
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://") ||
    imagePath.startsWith("data:")
  ) {
    return imagePath;
  }
  if (imagePath.startsWith("/")) {
    return imagePath;
  }
  const normalized = imagePath.replace(/^\.?\//, "");
  return `/assets/${normalized}`;
}

function appendCacheKey(url: string, cacheKey?: string): string {
  if (cacheKey == null || cacheKey.length === 0 || url.startsWith("data:")) {
    return url;
  }
  const hashIndex = url.indexOf("#");
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
  const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}v=${encodeURIComponent(cacheKey)}${hash}`;
}

function resolveImageUrlWithCacheKey(imagePath: string, cacheKey?: string): string {
  return appendCacheKey(resolveImageUrl(imagePath), cacheKey);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load atlas image: ${url}`));
    image.src = url;
  });
}

export class AssetManager {
  private manifest: AtlasManifest | null = null;
  private atlasImage: HTMLImageElement | null = null;
  private loadState: AssetLoadState = {
    ready: false,
    usingFallback: true,
    missingRequiredFrameIds: [...REQUIRED_SPRITE_IDS]
  };
  private frameClockSec = 0;
  private tileBlinkFps = 6;
  private rawFxSequenceFrames = new Map<FxSequenceId, HTMLImageElement[]>();
  private rawFxSequenceLoading = new Set<FxSequenceId>();

  private readonly fxSequenceFps: Record<string, number> = {
    fx_pop: 18,
    fx_explosion: 10,
    fx_wipewave: 16,
    fx_rotateflash: 14
  };

  setFrameClockSeconds(value: number): void {
    this.frameClockSec = value;
  }

  setTileBlinkFps(fps: number): void {
    this.tileBlinkFps = Math.max(0.1, fps);
  }

  getLoadState(): AssetLoadState {
    return {
      ready: this.loadState.ready,
      usingFallback: this.loadState.usingFallback,
      missingRequiredFrameIds: [...this.loadState.missingRequiredFrameIds],
      reason: this.loadState.reason,
      atlasImageUrl: this.loadState.atlasImageUrl
    };
  }

  async loadOptionalAtlas(): Promise<AssetLoadState> {
    this.manifest = null;
    this.atlasImage = null;
    this.loadState = {
      ready: false,
      usingFallback: true,
      missingRequiredFrameIds: [...REQUIRED_SPRITE_IDS]
    };

    try {
      const response = await fetch("/assets/atlas.json", { cache: "no-store" });
      if (!response.ok) {
        this.loadState.reason = `atlas.json not found (${response.status})`;
        return this.getLoadState();
      }

      const json = (await response.json()) as unknown;
      const manifest = parseTexturePackerHash(json);
      if (manifest == null) {
        this.loadState.reason = "atlas.json malformed or has no valid frames";
        return this.getLoadState();
      }

      const atlasImageUrl = resolveImageUrlWithCacheKey(manifest.imagePath, manifest.cacheKey);
      const atlasImage = await loadImage(atlasImageUrl);
      const missingRequiredFrameIds = REQUIRED_SPRITE_IDS.filter(
        (id) => getFrame(manifest, id) == null
      );

      this.manifest = manifest;
      this.atlasImage = atlasImage;
      this.loadState = {
        ready: true,
        usingFallback: false,
        missingRequiredFrameIds,
        atlasImageUrl
      };
      return this.getLoadState();
    } catch (error) {
      const message = error instanceof Error ? error.message : "atlas load failed";
      this.loadState.reason = message;
      return this.getLoadState();
    }
  }

  isAtlasReady(): boolean {
    return this.loadState.ready && this.manifest != null && this.atlasImage != null;
  }

  missingFrameIds(): string[] {
    return [...this.loadState.missingRequiredFrameIds];
  }

  private hasFrame(id: string): boolean {
    return this.manifest != null && getFrame(this.manifest, id) != null;
  }

  private resolveSequenceFrameId(baseId: string): string | null {
    if (!this.isAtlasReady() || this.manifest == null) {
      return null;
    }
    const fps = this.fxSequenceFps[baseId];
    if (fps == null) {
      return null;
    }
    const frames: string[] = [];
    for (let i = 1; i <= 32; i += 1) {
      const candidate = `${baseId}_${i}`;
      if (this.hasFrame(candidate)) {
        frames.push(candidate);
        continue;
      }
      if (frames.length > 0) {
        break;
      }
    }
    if (frames.length === 0) {
      return null;
    }
    const idx = Math.floor(this.frameClockSec * fps) % frames.length;
    return frames[idx];
  }

  resolveTimerIconId(emptyPct: number): SpriteId {
    const clamped = Math.max(0, Math.min(1, emptyPct));
    const preferred =
      clamped >= 0.76 ? "ui_timer_1"
      : clamped >= 0.51 ? "ui_timer_2"
      : clamped >= 0.26 ? "ui_timer_3"
      : "ui_timer_4";
    if (this.hasFrame(preferred)) {
      return preferred as SpriteId;
    }
    return "ui_timer";
  }

  resolveGrooveIconId(groovePct: number): SpriteId {
    const clamped = Math.max(0, Math.min(1, groovePct));
    const index = Math.min(16, Math.max(1, Math.floor(clamped * 16) + 1));
    const preferred = `ui_groove_${String(index).padStart(2, "0")}`;
    if (this.hasFrame(preferred)) {
      return preferred as SpriteId;
    }
    return "ui_groove";
  }

  private resolveAnimatedFrameId(id: SpriteId): string {
    if (!this.isAtlasReady() || this.manifest == null) {
      return id;
    }
    const sequenceFrame = this.resolveSequenceFrameId(id);
    if (sequenceFrame != null) {
      return sequenceFrame;
    }
    if (id.endsWith("_b")) {
      return id;
    }
    const variant = `${id}_b`;
    if (getFrame(this.manifest, variant) == null || getFrame(this.manifest, id) == null) {
      return id;
    }
    const frameIndex = Math.floor(this.frameClockSec * this.tileBlinkFps) % 2;
    return frameIndex === 0 ? id : variant;
  }

  private loadRawFxSequence(id: FxSequenceId): void {
    if (this.rawFxSequenceLoading.has(id) || this.rawFxSequenceFrames.has(id)) {
      return;
    }
    if (typeof Image !== "function") {
      return;
    }
    this.rawFxSequenceLoading.add(id);

    const frameNames = RAW_FX_SEQUENCE_FRAMES[id];
    const loaded: HTMLImageElement[] = [];
    let remaining = frameNames.length;

    const finish = (): void => {
      remaining -= 1;
      if (remaining > 0) {
        return;
      }
      this.rawFxSequenceLoading.delete(id);
      if (loaded.length > 0) {
        this.rawFxSequenceFrames.set(id, loaded);
      }
    };

    for (const frameName of frameNames) {
      const img = new Image();
      img.onload = () => {
        loaded.push(img);
        finish();
      };
      img.onerror = () => {
        finish();
      };
      img.src = resolveImageUrl(`raw/${frameName}.png`);
    }
  }

  private drawRawFxSequence(
    ctx: CanvasRenderingContext2D,
    id: SpriteId,
    dx: number,
    dy: number,
    dw: number,
    dh: number
  ): boolean {
    if (!isFxSequenceId(id)) {
      return false;
    }
    const frames = this.rawFxSequenceFrames.get(id);
    if (frames == null || frames.length === 0) {
      this.loadRawFxSequence(id);
      return false;
    }
    const fps = this.fxSequenceFps[id] ?? 12;
    const idx = Math.floor(this.frameClockSec * fps) % frames.length;
    const frame = frames[idx] ?? frames[0];
    if (frame == null) {
      return false;
    }
    ctx.drawImage(frame, dx, dy, dw, dh);
    return true;
  }

  drawSpriteOrFallback(
    ctx: CanvasRenderingContext2D,
    id: SpriteId,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
    fallbackDrawFn: () => void
  ): void {
    if (this.drawRawFxSequence(ctx, id, dx, dy, dw, dh)) {
      return;
    }
    if (!this.isAtlasReady() || this.manifest == null || this.atlasImage == null) {
      fallbackDrawFn();
      return;
    }
    const animated = this.resolveAnimatedFrameId(id);
    const frame = getFrame(this.manifest, animated) ?? getFrame(this.manifest, id);
    if (frame == null) {
      fallbackDrawFn();
      return;
    }

    ctx.drawImage(this.atlasImage, frame.x, frame.y, frame.w, frame.h, dx, dy, dw, dh);
  }
}
